/** @jsxImportSource @emotion/react */
import { useRef, useState, type ReactNode, type MouseEvent, type ChangeEvent } from 'react';
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import { ChartLegend } from '../../../design-system/molecules/ChartLegend/ChartLegend';
import { Cluster } from '../../../design-system/layout/Cluster';
import { theme } from '../../../design-system/theme/theme';
import type { ScheduleRow } from '../../types';
import {
  titleCaptionStyle,
  dateNavStyle,
  navBtnStyle,
  navDateStyle,
  todayBtnStyle,
  zoomCtrlStyle,
  zoomSliderStyle,
  axisRowStyle,
  nameColStyle,
  nameColClickableStyle,
  axisStyle,
  rowStyle,
  groupBreakStyle,
  trackStyle,
  gridlineStyle,
  nowLineStyle,
  selectionBandStyle,
  slotStyle,
} from './DailyScheduleBoard.styles';

export interface DailyScheduleBoardProps {
  rows: ScheduleRow[];
  /** 현재 시각(0~24). 트랙 위 세로선 위치. 오늘(isToday)일 때만 표시. */
  nowHour: number;
  /** 제목 옆 보조 문구 · 현재 선택 날짜. 예: "2026-07-30" (선택) */
  dateLabel?: ReactNode;
  /** 선택 날짜가 오늘인지 — now-line 표시·다음 날 버튼 비활성 판단. 기본 true. */
  isToday?: boolean;
  /** 이전 날 이동. 주어지면 날짜 이동 컨트롤 노출. */
  onPrevDay?: () => void;
  /** 다음 날 이동. */
  onNextDay?: () => void;
  /** 오늘로 되돌리기. */
  onToday?: () => void;
  /** 슬롯 클릭 → 해당 Job 상세로 이동. jobId가 있는 과거 막대만 클릭 가능. */
  onSlotClick?: (jobId: number) => void;
  /** 모듈명(행 이름) 클릭 → 모듈 관리 화면으로 이동해 해당 모듈로 스크롤. */
  onModuleClick?: (moduleName: string) => void;
}

/** 확대 안 한 기본 뷰(0~24시 전체). */
const FULL_VIEW = { start: 0, end: 24 };
/** 최대 확대 시 보이는 최소 구간(시). 슬라이더 오른쪽 끝 = 이 폭. */
const MIN_SPAN = 2;
/** − / + 버튼 한 번에 바꾸는 구간(시). */
const ZOOM_STEP = 2;
/** 드래그 확대 최소 구간(시). 이보다 좁게 선택하면 확대하지 않음(오클릭 방지). */
const MIN_ZOOM_HOURS = 0.5;
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** 시각(시)을 "H:MM"으로. 정시는 "H:00". 예: 8→"8:00", 9.5→"9:30". */
function formatHour(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}:${String(mm).padStart(2, '0')}`;
}

/**
 * 현재 뷰 구간에 맞는 눈금(시) 배열을 만든다.
 * 구간이 좁을수록 촘촘한 step을 골라 6~9개 안팎의 눈금이 보이게 한다.
 */
function makeTicks(start: number, end: number): number[] {
  const range = end - start;
  const step = range <= 1.5 ? 0.25 : range <= 3 ? 0.5 : range <= 6 ? 1 : range <= 12 ? 2 : 3;
  const ticks: number[] = [];
  const first = Math.ceil(start / step - 1e-9) * step;
  for (let h = first; h <= end + 1e-6; h += step) {
    ticks.push(Math.round(h * 100) / 100);
  }
  return ticks;
}

/** 편성표. 모듈별 스케줄 실행을 24시간 타임라인에 표시. 과거 날짜 조회 지원. */
export function DailyScheduleBoard({
  rows,
  nowHour,
  dateLabel,
  isToday = true,
  onPrevDay,
  onNextDay,
  onToday,
  onSlotClick,
  onModuleClick,
}: DailyScheduleBoardProps) {
  // 날짜 이동 핸들러가 주어지면 컨트롤을 노출한다.
  const hasDateNav = onPrevDay !== undefined || onNextDay !== undefined;

  // ── 시간축 확대 상태 ─────────────────────────────
  // view: 현재 화면에 보이는 시간 구간(시). 드래그로 좁히고 리셋으로 0~24 복귀.
  const [view, setView] = useState(FULL_VIEW);
  // sel: 드래그 중 선택 구간(시). null이면 드래그 아님.
  const [sel, setSel] = useState<{ from: number; to: number } | null>(null);
  // 드래그 진행 정보. rect·시작 시각·마지막 시각을 담아 window 리스너에서 참조.
  const dragRef = useRef<{ rect: DOMRect; startHour: number; lastHour: number } | null>(null);
  // 방금 드래그로 확대했는지 — 슬롯 클릭(상세 이동)과 확대 드래그를 구분.
  const draggedRef = useRef(false);

  const span = view.end - view.start;
  const ticks = makeTicks(view.start, view.end);

  // 시각(시) → 현재 뷰 기준 가로 위치(%).
  const xPct = (h: number) => ((h - view.start) / span) * 100;
  // clientX → 트랙 rect 기준 시각(시). 뷰 구간 안으로 clamp.
  const hourAt = (clientX: number, rect: DOMRect) => {
    const frac = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    return view.start + frac * span;
  };

  // 드래그 이동: window 리스너로 트랙 밖으로 나가도 추적.
  const handleDragMove = (e: globalThis.MouseEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const h = hourAt(e.clientX, d.rect);
    d.lastHour = h;
    if (Math.abs(h - d.startHour) >= 0.1) draggedRef.current = true;
    setSel({ from: d.startHour, to: h });
  };
  // 드래그 종료: 충분히 넓게 선택했으면 그 구간으로 확대.
  const handleDragEnd = () => {
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    const d = dragRef.current;
    dragRef.current = null;
    setSel(null);
    if (!d) return;
    const lo = Math.min(d.startHour, d.lastHour);
    const hi = Math.max(d.startHour, d.lastHour);
    if (hi - lo >= MIN_ZOOM_HOURS) setView({ start: lo, end: hi });
  };
  // 트랙에서 마우스 누름 → 드래그 확대 시작. (슬롯 위에서 눌러도 트랙으로 버블링)
  const handleTrackMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const startHour = hourAt(e.clientX, rect);
    dragRef.current = { rect, startHour, lastHour: startHour };
    draggedRef.current = false;
    setSel({ from: startHour, to: startHour });
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
  };
  const resetZoom = () => setView(FULL_VIEW);
  // 날짜를 바꾸면 확대를 자동 해제(다른 날 데이터는 전체 구간으로 다시 본다).
  const withReset = (fn?: () => void) =>
    fn ? () => { resetZoom(); fn(); } : undefined;

  // ── 돋보기 줌 컨트롤(−/슬라이더/+) ───────────────
  // 지정한 폭(시)으로, 현재 뷰 중심을 유지한 채 확대/축소. 0~24 밖으로 나가면 안쪽으로 민다.
  const setSpan = (newSpan: number) => {
    const s = clamp(newSpan, MIN_SPAN, 24);
    if (s >= 24) return resetZoom();
    const center = (view.start + view.end) / 2;
    let start = center - s / 2;
    let end = start + s;
    if (start < 0) { start = 0; end = s; }
    if (end > 24) { end = 24; start = 24 - s; }
    setView({ start, end });
  };
  // 슬라이더 값(0=전체, 1=최대 확대) ↔ 구간 폭.
  const zoomValue = (24 - span) / (24 - MIN_SPAN);
  const onZoomSlider = (e: ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    setSpan(24 - v * (24 - MIN_SPAN));
  };
  const zoomOut = () => setSpan(span + ZOOM_STEP); // − : 넓게(축소)
  const zoomIn = () => setSpan(span - ZOOM_STEP); // + : 좁게(확대)

  // 선택 밴드(드래그 중) 위치.
  const band = sel
    ? { left: xPct(Math.min(sel.from, sel.to)), width: Math.abs(xPct(sel.to) - xPct(sel.from)) }
    : null;

  return (
    <PanelCard
      collapsible
      title={
        <>
          편성표{' '}
          {!hasDateNav && dateLabel !== undefined && (
            <span css={titleCaptionStyle}>{dateLabel}</span>
          )}
        </>
      }
      headerRight={
        <Cluster gap="md">
          {/* 줌 컨트롤: − 슬라이더 + (드래그 확대도 병행 가능). 날짜 이동 시 자동 리셋. */}
          <div css={zoomCtrlStyle}>
            <button
              css={navBtnStyle}
              onClick={zoomOut}
              disabled={span >= 24}
              title="축소"
              aria-label="축소"
            >
              −
            </button>
            <input
              css={zoomSliderStyle}
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={clamp(zoomValue, 0, 1)}
              onChange={onZoomSlider}
              title="시간대 확대"
              aria-label="시간대 확대 정도"
            />
            <button
              css={navBtnStyle}
              onClick={zoomIn}
              disabled={span <= MIN_SPAN}
              title="확대"
              aria-label="확대"
            >
              ＋
            </button>
          </div>
          {hasDateNav && (
            <div css={dateNavStyle}>
              {/* Today 버튼은 항상 렌더(오늘이면 숨김) → 네비게이션이 밀리지 않음 */}
              <button css={todayBtnStyle(isToday)} onClick={withReset(onToday)} disabled={isToday}>
                Today
              </button>
              <button css={navBtnStyle} onClick={withReset(onPrevDay)} title="이전 날" aria-label="이전 날">
                ◂
              </button>
              <span css={navDateStyle}>{dateLabel}</span>
              <button
                css={navBtnStyle}
                onClick={withReset(onNextDay)}
                disabled={isToday}
                title="다음 날"
                aria-label="다음 날"
              >
                ▸
              </button>
            </div>
          )}
          <ChartLegend
            items={[
              { color: theme.color.success, label: '성공' },
              { color: theme.color.success2, label: '재시도 성공' },
              { color: theme.color.danger, label: '실패' },
              { color: theme.color.running, label: '진행' },
              { color: theme.color.surface, label: '예정', outline: true },
            ]}
          />
        </Cluster>
      }
    >
      <div css={axisRowStyle}>
        <div css={nameColStyle} />
        <div css={axisStyle}>
          {ticks.map((h) => {
            const pct = xPct(h);
            // 좌우 끝 눈금은 축 밖으로 안 나가게 정렬 보정.
            const transform =
              pct <= 0.01 ? 'translateX(0)' : pct >= 99.99 ? 'translateX(-100%)' : 'translateX(-50%)';
            return (
              <span key={h} style={{ left: `${pct}%`, transform }}>
                {formatHour(h)}
              </span>
            );
          })}
        </div>
      </div>
      {rows.map((row) => (
        <div key={row.name} css={[rowStyle, row.groupBreak && groupBreakStyle]}>
          <div
            css={[nameColStyle, onModuleClick && nameColClickableStyle]}
            onClick={onModuleClick ? () => onModuleClick(row.name) : undefined}
            title={onModuleClick ? `${row.name} 모듈 관리로 이동` : undefined}
          >
            {row.name}
          </div>
          <div css={trackStyle} onMouseDown={handleTrackMouseDown}>
            {ticks.map((h) => (
              <span key={h} css={gridlineStyle} style={{ left: `${xPct(h)}%` }} />
            ))}
            {/* now-line은 오늘 볼 때만. 확대로 뷰 밖이면 숨김. */}
            {isToday && nowHour >= view.start && nowHour <= view.end && (
              <span css={nowLineStyle} style={{ left: `${xPct(nowHour)}%` }} title="현재" />
            )}
            {/* 드래그 확대 선택 밴드 (모든 행에 같은 위치로 그려져 세로 밴드처럼 보임). */}
            {band && (
              <span css={selectionBandStyle} style={{ left: `${band.left}%`, width: `${band.width}%` }} />
            )}
            {row.slots.map((slot, i) => {
              // duration이 있으면 막대(started 위치 ~ duration만큼 width), 없으면 점
              const hasDuration = slot.durationHours !== undefined;
              const leftPct = xPct(slot.hour);
              // 폭은 현재 뷰 구간(span) 대비 비율. 확대할수록 막대가 넓어진다.
              const widthPct = hasDuration ? (slot.durationHours! / span) * 100 : undefined;
              // 확대로 뷰 완전히 벗어난 슬롯은 건너뛴다(트랙 overflow가 부분 클립 처리).
              if (hasDuration) {
                if (leftPct + widthPct! <= 0 || leftPct >= 100) return null;
              } else if (leftPct < 0 || leftPct > 100) {
                return null;
              }
              const clickable = onSlotClick !== undefined && slot.jobId !== undefined;
              return (
                <span
                  key={i}
                  css={slotStyle(slot.state, widthPct, clickable, slot.manual)}
                  style={{ left: hasDuration ? `${leftPct}%` : `calc(${leftPct}% - 5px)` }}
                  title={slot.tooltip}
                  // 드래그로 확대한 직후의 클릭은 무시(상세 이동 방지).
                  onClick={
                    clickable
                      ? () => {
                          if (draggedRef.current) return;
                          onSlotClick!(slot.jobId!);
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
        </div>
      ))}
    </PanelCard>
  );
}
